import { sanitizeLogData, sanitizeLogString } from './logSanitizer';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
  context?: string;
}

class Logger {
  private isProduction = import.meta.env.PROD;
  private logHistory: LogEntry[] = [];
  private maxHistorySize = 100;

  private formatMessage(level: LogLevel, message: string, _data?: any, context?: string): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? `[${context}]` : '';
    return `${timestamp} ${level.toUpperCase()} ${contextStr} ${message}`;
  }

  private addToHistory(level: LogLevel, message: string, data?: any, context?: string): void {
    // Sanitize data before storing in history
    const sanitizedData = data ? sanitizeLogData(data) : undefined;
    const sanitizedMessage = sanitizeLogString(message);
    
    const entry: LogEntry = {
      level,
      message: sanitizedMessage,
      data: sanitizedData,
      timestamp: new Date().toISOString(),
      context
    };

    this.logHistory.push(entry);
    
    // Keep history size manageable
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isProduction) {
      // In production, only log warnings and errors
      return level === 'warn' || level === 'error';
    }
    // In development, log everything
    return true;
  }

  debug(message: string, data?: any, context?: string): void {
    if (!this.shouldLog('debug')) return;
    
    // Sanitize data before logging
    const sanitizedData = data ? sanitizeLogData(data) : undefined;
    const sanitizedMessage = sanitizeLogString(message);
    
    const formattedMessage = this.formatMessage('debug', sanitizedMessage, sanitizedData, context);
    console.debug(formattedMessage, sanitizedData || '');
    this.addToHistory('debug', sanitizedMessage, sanitizedData, context);
  }

  info(message: string, data?: any, context?: string): void {
    if (!this.shouldLog('info')) return;
    
    // Sanitize data before logging
    const sanitizedData = data ? sanitizeLogData(data) : undefined;
    const sanitizedMessage = sanitizeLogString(message);
    
    const formattedMessage = this.formatMessage('info', sanitizedMessage, sanitizedData, context);
    console.info(formattedMessage, sanitizedData || '');
    this.addToHistory('info', sanitizedMessage, sanitizedData, context);
  }

  warn(message: string, data?: any, context?: string): void {
    if (!this.shouldLog('warn')) return;
    
    // Sanitize data before logging
    const sanitizedData = data ? sanitizeLogData(data) : undefined;
    const sanitizedMessage = sanitizeLogString(message);
    
    const formattedMessage = this.formatMessage('warn', sanitizedMessage, sanitizedData, context);
    console.warn(formattedMessage, sanitizedData || '');
    this.addToHistory('warn', sanitizedMessage, sanitizedData, context);
  }

  error(message: string, data?: any, context?: string): void {
    if (!this.shouldLog('error')) return;
    
    // Sanitize data before logging
    const sanitizedData = data ? sanitizeLogData(data) : undefined;
    const sanitizedMessage = sanitizeLogString(message);
    
    const formattedMessage = this.formatMessage('error', sanitizedMessage, sanitizedData, context);
    console.error(formattedMessage, sanitizedData || '');
    this.addToHistory('error', sanitizedMessage, sanitizedData, context);
  }

  // Get recent log history for debugging
  getHistory(level?: LogLevel, limit = 50): LogEntry[] {
    let filtered = this.logHistory;
    
    if (level) {
      filtered = this.logHistory.filter(entry => entry.level === level);
    }
    
    return filtered.slice(-limit);
  }

  // Clear log history
  clearHistory(): void {
    this.logHistory = [];
  }

  // Export logs for debugging (development only)
  exportLogs(): string {
    if (this.isProduction) {
      this.warn('Log export not available in production');
      return '';
    }
    
    return JSON.stringify(this.logHistory, null, 2);
  }
}

// Create singleton instance
export const logger = new Logger();

// Export types for use in other files
export type { LogLevel, LogEntry };

